// tslint:disable: max-file-line-count

import { EBuildingStatus } from '../../../../repositories/common/json_query';
import { Button, ButtonTheme, CheckboxField } from '@cian/components';
import { mergeStyles } from '@cian/utils';
import * as React from 'react';
import { debounce } from 'lodash';

import { ILocation } from '../../../../api/locations';
import { ButtonGroup } from '../../../../common/components/button_group';
import { FromToControl } from '../../../../common/components/from_to_control';
import { IJsonQueryBackup } from '../../../../common/state/app_state';
import { IJsonQuery } from '../../../../json_query';
import { getTermValue } from '../../../../json_query/reducers/helpers';
import {
  dealTypeFromJsonQuery,
  FDealType,
  FOfferType,
  isCommercial,
  isParamAvailable,
  offerTypeFromJsonQuery,
} from '../../../../utils/category';
import { commercialOffRepRegions, getRegionId, isGeoAvailable, moscowMoIds, spbLoIds } from '../../../../utils/geo';
import { normalizeUndefined } from '../../../../utils/normalize';
import FromMKAD from '../../advanced/params/from_mkad';
import SiteArea from '../../advanced/params/site_area';
import { GeoSuggestContainer } from '../../geo_suggest/container';
import { isHighwaysEnabled } from '../../helpers';
import LocationSwitherButtonContainer from '../../location_switcher_button/container';
import { SaveContainer } from '../../save';
import { SearchButtonContainer } from '../../search_button/container';
import { trackOpenDistrictsModalEvent } from './tracking';
import { DealOfferRoomsContainer } from '../../deal_offer_rooms/container';
import { NewbuildingGkButton } from '../ab/newbuilding_gk_button';
import { ObjectTypeContainer } from '../../ObjectType/container';
import { WithNeighborsContainer } from '../../WithNeighbors/container';
import { CurrencyTypeContainer } from '../../currency_type/container';
import { AreaContainer } from '../../area/container';
import { PriceTypeContainer } from '../../price_type/container';
import { CoworkingPriceDesc } from '../../CoworkingPriceDesc';
import { IsByCommercialOwner } from '../../IsByCommercialOwner';

import * as styles from './mainFilters.css';
import { ITagsOwnProps } from '../../tags';

export interface IMainFiltersProps {
  backup?: IJsonQueryBackup;
  jsonQuery: IJsonQuery;
  jsonQueryCount?: number;
  currentLocation: ILocation | 'moscow_mo' | 'spb_lo';

  tags: React.ComponentClass<ITagsOwnProps>;

  withPhoto: boolean;

  minPrice?: number;
  minWorkPlaceCount?: number;
  onMinPriceChanged(minPrice?: number): void;
  onMinWorkPlaceCountChanged(minPrice?: number): void;

  maxPrice?: number;
  maxWorkPlaceCount?: number;
  onMaxPriceChanged(minPrice?: number): void;
  onMaxWorkPlaceCountChanged(maxPrice?: number): void;

  onAdvancedFiltersButtonClick(): void;

  hasMetro: boolean;
  isLocationHasDistricts: boolean;

  isAdvancedFiltersOpened?: boolean;

  onIsByHomeownerChanged(value: boolean): void;
  onIsDealRentChanged(value: boolean): void;
  onIsFromDeveloperChanged(value?: boolean): void;
  onFromOffRepChanged(value: boolean): void;
  onMetroOpenButtonClick(): void;
  onDistrictClick(): void;
  onSearchButtonClick(): void;
  openDirectionsModal(): void;

  isFromDeveloperExperiment: boolean;

  isFetching: boolean;

  isCommercialOwnerFilterAvailable: boolean;
}

export interface IMainFiltersState {
  shouldCompressFilters: boolean;
  shouldCompressObjectType: boolean;
}

export class MainFilters extends React.Component<IMainFiltersProps, IMainFiltersState> {
  private isParamAvailable: (allowedDealTypes: FDealType, allowedOfferTypes: FOfferType, custom?: boolean) => boolean;

  public state = {
    shouldCompressFilters: false,
    shouldCompressObjectType: false,
  };

  public componentDidMount() {
    this.handleWindowResize();
    window.addEventListener('resize', this.handleWindowResize);
  }

  public componentWillUnmount() {
    window.removeEventListener('resize', this.handleWindowResize);
  }

  public componentWillReceiveProps(nextProps: IMainFiltersProps) {
    if (this.props.jsonQuery.building_status !== nextProps.jsonQuery.building_status) {
      setTimeout(this.handleWindowResize, 0);
    }
  }

  public render() {
    const props = this.props;
    const { isCommercialOwnerFilterAvailable } = props;

    const offerType = offerTypeFromJsonQuery(props.jsonQuery);
    const dealType = dealTypeFromJsonQuery(props.jsonQuery);

    this.isParamAvailable = isParamAvailable(dealType, offerType);

    const isFromMKADAvailable = isGeoAvailable(moscowMoIds, props.currentLocation);

    const priceType = (this.isParamAvailable(FDealType.Sale, FOfferType.Urban) ||
      this.isParamAvailable(FDealType.Any, FOfferType.Commercial & ~FOfferType.Coworking)) && (
      <div style={{ display: 'inline-block' }} data-mark="priceType">
        <PriceTypeContainer />
      </div>
    );

    const currencyType = this.isParamAvailable(FDealType.Any, FOfferType.Commercial & ~FOfferType.Coworking) && (
      <div style={{ display: 'inline-block' }} data-mark="currencyType">
        <CurrencyTypeContainer />
      </div>
    );

    const coworkingPriceDesc = this.isParamAvailable(FDealType.Any, FOfferType.Coworking) && (
      <div className={styles['coworking-price-desc-container']} data-mark="coworkingPriceDesc">
        <CoworkingPriceDesc />
      </div>
    );

    const area = (this.isParamAvailable(
      FDealType.Any,
      FOfferType.Commercial & ~FOfferType.CommercialLand & ~FOfferType.Coworking,
    ) ||
      this.isParamAvailable(FDealType.Any, FOfferType.Suburban & ~FOfferType.Land, !isFromMKADAvailable)) && (
      <div {...mergeStyles(styles['area-filter'], styles['filters-item'])}>
        Площадь:&nbsp;&nbsp;
        <div style={{ display: 'inline-block' }} data-mark="areaField">
          <AreaContainer />
        </div>
        &nbsp;&nbsp;{'м\u00B2'}
      </div>
    );

    const fromMKAD = (offerType & FOfferType.Suburban) !== 0 && isFromMKADAvailable && (
      <div {...mergeStyles(styles['from-mkad-filter'], styles['filters-item'])}>
        От МКАД:&nbsp;&nbsp;
        <div style={{ display: 'inline-block' }} data-mark="fromMKAD">
          <FromMKAD key="FromMKAD" shortView={true} />
        </div>
        &nbsp;&nbsp;км
      </div>
    );

    const landArea = (offerType === FOfferType.CommercialLand ||
      (offerType === FOfferType.Land && !isFromMKADAvailable)) && (
      <div {...mergeStyles(styles['land-area-filter'], styles['filters-item'])}>
        Площадь:&nbsp;&nbsp;
        <div style={{ display: 'inline-block' }} data-mark="landArea">
          <SiteArea shortView={true} />
        </div>
        &nbsp;&nbsp;сот.
      </div>
    );

    const { shouldCompressFilters, shouldCompressObjectType } = this.state;

    const regionId = getRegionId(props.currentLocation);

    const isByHomeOwner = Boolean(getTermValue('is_by_homeowner')(props.jsonQuery));
    const isDealRent = Boolean(getTermValue('deal_rent')(props.jsonQuery));
    const isFromDeveloper = Boolean(getTermValue('from_developer')(props.jsonQuery));
    const isFromOffrep = Boolean(getTermValue('from_offrep')(props.jsonQuery));
    const offerTypesForOffrep =
      FOfferType.Office | FOfferType.Warehouse | FOfferType.TradeArea | FOfferType.FreeAppointmentObject;
    const isFromOffrepVisible =
      isCommercial(offerType) && offerType & offerTypesForOffrep && commercialOffRepRegions.some(r => r === regionId);
    const isDealRentVisible =
      dealType === FDealType.RentLongterm &&
      offerType === FOfferType.FlatOld &&
      [...moscowMoIds, ...spbLoIds, -1, -2].includes(regionId);
    const isByHomeOwnerLabel = shouldCompressFilters ? 'От собств.' : 'От собственника';
    const isFromDeveloperLabel = shouldCompressFilters ? 'От застр.' : 'От застройщика';
    const { isFetching, jsonQueryCount, jsonQuery } = this.props;
    const isSaveButtonActive = isFetching || typeof jsonQueryCount === 'undefined';

    const shouldRenderObjectType =
      dealType === FDealType.Sale && [FOfferType.Flat, FOfferType.FlatNew, FOfferType.FlatOld].includes(offerType);

    const isByHomeOwnerVisible = (offerType & (FOfferType.Residential & ~FOfferType.FlatNew)) !== 0;
    const isFromDeveloperVisible = offerType === FOfferType.FlatNew;

    const isCoworking = jsonQuery.coworking_offer_type && jsonQuery.coworking_offer_type.value.length > 0;
    const isCommercialOwnerFilterVisible = isCommercialOwnerFilterAvailable && isCommercial(offerType);
    const isAvailableCommercialOwnerOfferType = offerType !== FOfferType.Coworking && offerType !== FOfferType.Garage;

    return (
      <div className={styles['filters']}>
        <div className={styles['filters-container']}>
          <div className={styles['filters-row']}>
            <div className={styles['filters-item']}>
              <DealOfferRoomsContainer shouldCompressFilters={shouldCompressObjectType} />
            </div>
            {shouldRenderObjectType && (
              <div className={styles['filters-item']}>
                <ObjectTypeContainer />
              </div>
            )}
            {isCoworking && (
              <div {...mergeStyles(styles['filters-item'], styles['workplace'])} data-mark="workplaceField">
                Кол-во рабочих мест:&nbsp;&nbsp;
                <FromToControl
                  rangeGroupStyle={styles['workplace-count-input']}
                  from={{
                    value: props.minWorkPlaceCount,
                    placeholder: 'от',
                    onValueChange: value => props.onMinWorkPlaceCountChanged(normalizeUndefined(value)),
                  }}
                  to={{
                    value: props.maxWorkPlaceCount,
                    placeholder: 'до',
                    onValueChange: value => props.onMaxWorkPlaceCountChanged(normalizeUndefined(value)),
                  }}
                />
              </div>
            )}
            <div {...mergeStyles(styles['filters-item'], styles['price'])} data-mark="priceField">
              Цена:&nbsp;&nbsp;
              <FromToControl
                rangeGroupStyle={styles['price-input']}
                from={{
                  value: props.minPrice,
                  placeholder: 'от',
                  onValueChange: value => props.onMinPriceChanged(normalizeUndefined(value)),
                }}
                to={{
                  value: props.maxPrice,
                  placeholder: 'до',
                  onValueChange: value => props.onMaxPriceChanged(normalizeUndefined(value)),
                }}
              />
              {currencyType}
              {priceType}
              {coworkingPriceDesc}
            </div>

            {area}
            {fromMKAD}
            {landArea}

            {isByHomeOwnerVisible && (
              <div className={styles['filters-item']}>
                <CheckboxField
                  label={isByHomeOwnerLabel}
                  value={isByHomeOwner}
                  onValueChange={props.onIsByHomeownerChanged.bind(null, !isByHomeOwner)}
                />
              </div>
            )}

            {isDealRentVisible && (
              <div className={styles['filters-item']}>
                <CheckboxField
                  label="С низкой комиссией"
                  value={isDealRent}
                  onValueChange={props.onIsDealRentChanged.bind(null, !isDealRent)}
                />
              </div>
            )}

            {isFromDeveloperVisible && props.isFromDeveloperExperiment && (
              <div className={styles['filters-item']}>
                <CheckboxField
                  label={isFromDeveloperLabel}
                  value={isFromDeveloper}
                  onValueChange={() => props.onIsFromDeveloperChanged(isFromDeveloper ? undefined : true)}
                />
              </div>
            )}

            {isCommercialOwnerFilterVisible && isAvailableCommercialOwnerOfferType && (
              <div className={styles['filters-item']}>
                <IsByCommercialOwner jsonQuery={jsonQuery} />
              </div>
            )}

            {!isCommercialOwnerFilterVisible && Boolean(isFromOffrepVisible) && (
              <div className={styles['filters-item']}>
                <CheckboxField
                  label="От оф. представителя"
                  value={isFromOffrep}
                  onValueChange={props.onFromOffRepChanged.bind(null, !isFromOffrep)}
                />
              </div>
            )}
          </div>
          <div className={styles['filters-row']}>
            <div
              {...mergeStyles(styles['filters-item'], styles['filters-item--location'])}
              data-mark="locationSwitcher"
            >
              <LocationSwitherButtonContainer />
            </div>
            <ButtonGroup className={styles['filters-item']}>
              {this.props.hasMetro && (
                <Button
                  buttonStyle={styles['button']}
                  dataMark="metro"
                  theme={ButtonTheme.BLUE}
                  onClick={this.props.onMetroOpenButtonClick}
                >
                  Метро
                </Button>
              )}
              {this.props.isLocationHasDistricts && (
                <Button
                  buttonStyle={styles['button']}
                  dataMark="district"
                  theme={ButtonTheme.BLUE}
                  onClick={this.openDistrictsModal}
                >
                  Район
                </Button>
              )}
              {isHighwaysEnabled(offerType, this.props.currentLocation) && (
                <Button
                  buttonStyle={styles['button']}
                  dataMark="highways"
                  theme={ButtonTheme.BLUE}
                  onClick={this.props.openDirectionsModal}
                >
                  Шоссе
                </Button>
              )}
            </ButtonGroup>
            <div {...mergeStyles(styles['filters-item'], styles['geoSuggest'])} data-mark="geoSuggest">
              <GeoSuggestContainer />
            </div>
            <WithNeighborsContainer />
            <div className={styles['filters-item']} data-mark="advancedButton">
              <Button
                buttonStyle={styles['advanced']}
                theme={ButtonTheme.BLUE}
                onClick={props.onAdvancedFiltersButtonClick}
              >
                {shouldCompressFilters ? 'Фильтры' : 'Ещё фильтры'}
              </Button>
            </div>
            <div className={styles['left-button-wrapper']}>
              <div className={styles['filters-item']} data-mark="searchButton">
                <SearchButtonContainer
                  isAdvancedFiltersOpened={this.props.isAdvancedFiltersOpened}
                  isCountHidden={Boolean(this.props.backup)}
                  onClick={this.handleClickSearchButton}
                  theme={isSaveButtonActive ? ButtonTheme.BLUELIGHT_SOLID : ButtonTheme.BLUE_SOLID}
                />
              </div>
            </div>
            <NewbuildingGkButton />
            <div className={styles['right-button-wrapper']}>
              <SaveContainer
                mode={isSaveButtonActive ? undefined : 'light'}
                isAdvancedFiltersOpened={this.props.isAdvancedFiltersOpened}
              />
            </div>
          </div>
          {this.renderTags()}
        </div>
      </div>
    );
  }

  private renderTags() {
    if (!this.props.tags) {
      return null;
    }

    return (
      <div className={styles['filters-row']}>
        <this.props.tags isAdvancedFiltersOpened={this.props.isAdvancedFiltersOpened} />
      </div>
    );
  }

  private handleClickSearchButton = () => {
    this.props.onSearchButtonClick();
  };

  private openDistrictsModal = () => {
    trackOpenDistrictsModalEvent();
    this.props.onDistrictClick();
  };

  private handleWindowResize = debounce(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const { building_status } = this.props.jsonQuery;
    const buildingStatusValue = building_status ? building_status.value : -1;

    const shouldCompressFilters = window.innerWidth <= 1150;
    const shouldCompressObjectType = window.innerWidth <= (buildingStatusValue === EBuildingStatus.New ? 1024 : 1250);
    if (
      shouldCompressFilters !== this.state.shouldCompressFilters ||
      shouldCompressObjectType !== this.state.shouldCompressObjectType
    ) {
      this.setState({ shouldCompressFilters, shouldCompressObjectType });
    }
  }, 100);
}
